import React, { useEffect, useState, useRef, useCallback } from "react";
import "./messagesPage.css";
import AdminBDRSpinner from "../../adminComponents/AdminBDRSpinner/AdminBDRSpinner";
import { GET_PHONE_INFO_FOR_TEXTS } from "../../../../utils/GraphQL/queries";
import { useLazyQuery } from "@apollo/client";
// import ClientSearchInput from "../../adminComponents/ClientSearchInput/ClientSearchInput";
import { useNavigate } from "react-router";
import MsgPageTextDisplay from "../../adminComponents/MsgPageTextDisplay/MsgPageTextDisplay";
/* A kebab, not a hamburger: this opens one row's options, not a nav. MUI's
   `IconButton` is gone — it brought its own chrome and theming next to the V2
   kit; the GLYPH stays an MUI icon, which is what `.bdr-btn .MuiSvgIcon-root`
   in index.css is written for. */
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MsgPageContactOptions from "../../adminComponents/MsgPageContactOptions/MsgPageContactOptions";
import ClientFormModal from "../../adminComponents/ClientForm/ClientFormModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import useNavHeightVar from "../../../../utils/useNavHeightVar";
import {
  useSSEListener,
  SSE_RECONNECTED,
} from "../../../../utils/SSE/SSEContext";

const MessagesPage = () => {
  const navigate = useNavigate();
  /* Publishes the nav's measured height as `--bdr-nav-h`, which the page
     shell subtracts in CSS. This REPLACES a `window.innerHeight - navHeight`
     read that ran once, 80ms after mount, with no resize listener — so the
     page kept its first height forever and a rotated phone clipped the
     composer off-screen behind `overflow: hidden`. The hook re-measures. */
  useNavHeightVar();

  const [loadError, setLoadError] = useState("");
  const [msgLoading, setMsgLoading] = useState(true);
  const [phoneNumArr, setPhoneNumArr] = useState([]);
  // Mirrors phoneNumArr for event handlers that need the current list
  // WITHOUT reading it inside a setState updater (updaters must stay pure).
  const phoneNumArrRef = useRef([]);
  phoneNumArrRef.current = phoneNumArr;
  const [queryPhoneMsgTrigger, setQueryPhoneMsgTrigger] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [createNewClientDisplay, setCreateNewClientDisplay] = useState(false);
  /* P4: the thread whose owner list is open in a modal (null = closed). Held
     as the thread object, not an id, so the modal does not have to re-find it
     in a list that re-sorts on every SSE event. */
  const [contactsFor, setContactsFor] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  /*
   * THE ROW WHOSE KEBAB IS OPEN — not the selected thread (plan §4.2).
   *
   * Every item in the contact menu used to act on `selectedId`, which the
   * kebab never sets (it calls stopPropagation, so the row's own onClick does
   * not run). So opening the menu on row B while thread A was selected acted
   * on A: "Create New Client" adopted A's number, and worse, "Assign To
   * Client" / "Move To X Only" REWROTE ownership of A's number on an existing
   * client. Setting `selectedId` from the kebab is not the fix — that would
   * switch the displayed thread on every menu open.
   */
  const [menuPhn, setMenuPhn] = useState(null);
  const [createClientPhnObj, setCreateClientPhnObj] = useState({});
  // Mirror selectedId into a ref so the SSE callback (registered once) always
  // reads the CURRENT selection instead of a stale closure value.
  const selectedIdRef = useRef("");
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);
  const [queryPhonesGQLCall] = useLazyQuery(GET_PHONE_INFO_FOR_TEXTS, {
    fetchPolicy: "network-only",
  });
  useEffect(() => {
    window.scrollTo(0, 0);
    queryStrippedPhoneInfo(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 60s focused-only backstop poll — the guaranteed floor under SSE. Only
  // refetches when a thread is open (a missed inbound on a focused thread is
  // the visible-regression case); the inbox itself recovers on SSE reconnect.
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedIdRef.current) {
        setQueryPhoneMsgTrigger((prev) => prev + 1);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // SSE: a new inbound text arrived. Reads selection from the ref (no stale
  // closure). Focused thread → bump the thread trigger so MsgPageTextDisplay
  // does an incremental since-fetch + append. Not focused → reorder the number
  // to the top and set its unread dot purely from the payload (no fetch).
  const handleSmsReceived = useCallback((data) => {
    if (!data?.phoneNumId) return;

    if (data.phoneNumId === selectedIdRef.current) {
      setQueryPhoneMsgTrigger((prev) => prev + 1);
    }

    /*
     * B4: the exists-check fetch stays OUTSIDE the updater.
     *
     * This fired `queryStrippedPhoneInfo` from INSIDE `setPhoneNumArr`'s
     * updater. Updaters must be pure, and React 18 StrictMode double-invokes
     * them — so a brand-new inbound conversation fired two full inbox
     * queries. `handleSmsSent` below already had this hoisted out, with a
     * sign-off comment saying exactly this; this handler was left behind.
     */
    if (!phoneNumArrRef.current?.some((obj) => obj?._id === data.phoneNumId)) {
      queryStrippedPhoneInfo(false);
      return;
    }

    setPhoneNumArr((prev) => {
      const focused = data.phoneNumId === selectedIdRef.current;
      const update = prev.map((obj) => {
        if (obj?._id !== data.phoneNumId) return obj;
        return {
          ...obj,
          recentTime: data.recentTime ?? obj?.recentTime,
          // Don't raise the dot on the thread the user is actively viewing —
          // the focused thread marks itself read on fetch.
          unreadMessages: focused ? false : true,
        };
      });

      update.sort((a, b) => parseInt(b?.recentTime) - parseInt(a?.recentTime));
      return [...update];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SSE reconnect/backfill: pull a fresh inbox so any numbers/dots missed
  // while the stream was down are reconciled. The unread reconcile is
  // server-authoritative here (useUnreadSearch:false) and won't blanket-clear
  // a freshly-arrived dot because the focused thread is the only place we set
  // unread:false locally.
  const handleSseReconnected = useCallback(() => {
    queryStrippedPhoneInfo(false);
    if (selectedIdRef.current) {
      setQueryPhoneMsgTrigger((prev) => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SSE: someone (any admin/office user, any device) read a thread —
  // shared-inbox semantics (office comms Phase 1). LOCAL STATE ONLY: clear
  // that thread's dot; NEVER call a mark-read mutation from here (the
  // server->client->server echo would loop — invariant D3 F5).
  const handleSmsRead = useCallback((data) => {
    if (!data?.phoneNumId) return;
    setPhoneNumArr((prev) =>
      prev?.map((obj) =>
        obj?._id === data.phoneNumId ? { ...obj, unreadMessages: false } : obj
      )
    );
  }, []);

  // SSE: an outbound was sent from another device (e.g. office mobile) —
  // reorder that thread to the top by the send's server recentTime. LOCAL
  // STATE ONLY (no fetch, no dot change — outbound never touches unread).
  // The originating tab's own send already reordered via handlePhnUpdate;
  // re-sorting on the echo is idempotent. A thread not in our list (outside
  // the 80-thread window) gets a fresh inbox pull, mirroring
  // handleSmsReceived's brand-new-conversation path.
  const handleSmsSent = useCallback((data) => {
    if (!data?.phoneNumId) return;
    // Cross-surface append (Taylor 2026-09-04): a send from ANOTHER surface
    // (mobile) must appear in an open web thread immediately, not on the 60s
    // backstop — the coordination value is seeing the reply exists NOW. The
    // since-fetch is _id-deduped, so the echo of our OWN send is a no-op.
    if (data.phoneNumId === selectedIdRef.current) {
      setQueryPhoneMsgTrigger((prev) => prev + 1);
    }
    // Sign-off F3: the exists-check fetch stays OUTSIDE the updater
    // (updaters must be pure; StrictMode double-invokes them).
    if (!phoneNumArrRef.current?.some((obj) => obj?._id === data.phoneNumId)) {
      queryStrippedPhoneInfo(false);
      return;
    }
    setPhoneNumArr((prev) => {
      const exists = prev?.some((obj) => obj?._id === data.phoneNumId);
      if (!exists) {
        return prev;
      }
      const update = prev.map((obj) =>
        obj?._id === data.phoneNumId
          ? { ...obj, recentTime: data.recentTime ?? obj?.recentTime }
          : obj
      );
      update.sort((a, b) => parseInt(b?.recentTime) - parseInt(a?.recentTime));
      return [...update];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSSEListener("sms_received", handleSmsReceived);
  useSSEListener("sms_read", handleSmsRead);
  useSSEListener("sms_sent", handleSmsSent);
  useSSEListener(SSE_RECONNECTED, handleSseReconnected);

  const navClient = (id) => {
    navigate(`/bdr-office/clients/view-client/${id}`);
  };

  // Clear the unread dot locally when a number is opened. The thread itself
  // marks-read server-side on fetch; this keeps the inbox in sync immediately.
  const clearUnreadLocal = (id) => {
    setPhoneNumArr((prev) =>
      prev?.map((obj) =>
        obj?._id === id ? { ...obj, unreadMessages: false } : obj
      )
    );
  };

  /*
   * §4.3 — SNAPSHOT, and the ordering matters.
   *
   * The menu item runs `handleNewClientDisplay(true)` and THEN
   * `handleClose(true)`, which clears `menuPhn`. Copying here, on the way in,
   * is what makes that safe: the modal is handed a value, not a live
   * reference, so the clear a tick later cannot turn it into a phoneless
   * create. The modal snapshots it again on open for the same reason.
   */
  const handleNewClientDisplay = (bool) => {
    setCreateNewClientDisplay(bool);
    if (bool === true) {
      setCreateClientPhnObj(menuPhn ? { ...menuPhn } : {});
      return;
    }
    setCreateClientPhnObj({});
  };

  /*
   * B1: the spinner used to be able to hang FOREVER.
   *
   * This had no try/catch and no final else, so an error (or anything that
   * left `data` undefined) fell past both branches and never called
   * `setMsgLoading(false)`. `AdminBDRSpinner` is position:fixed at z-index
   * 1500, so that is not "still loading" — it is a covered, unusable page
   * until a reload. WEB_CHANGELOG.md:3272 recorded this and it was never
   * fixed.
   *
   * `finally` is the fix: the flag is cleared on EVERY exit, including the
   * ones nobody thought of.
   */
  const queryStrippedPhoneInfo = async (bool) => {
    try {
      setLoadError("");
      const { data, error } = await queryPhonesGQLCall({
        variables: {
          useUnreadSearch: bool,
        },
      });

      /*
       * `error` is READ FROM THE RESULT, not caught.
       *
       * `useLazyQuery`'s execute promise RESOLVES on a GraphQL error rather
       * than rejecting (@apollo/client 3.8+), so a failure arrives as
       * `{ data: undefined, error: ApolloError }` and NEVER reaches the catch
       * below. Relying on the catch threw the server's real message away and
       * showed a generic string — and the two likeliest causes here both have
       * good messages: an expired web JWT (web has no refresh, 10h hard) gives
       * UNAUTHENTICATED, and a refinisher hitting this admin/office resolver
       * gives FORBIDDEN. The catch stays for a synchronous throw; `finally` is
       * what actually killed the hang.
       */
      if (error) {
        console.error("Messages inbox load failed:", error.message);
        setLoadError(error.message || "Could not load conversations.");
        return;
      }

      if (data?.getPhonesInfoForText === null) {
        return;
      }

      if (!data?.getPhonesInfoForText) {
        setLoadError("Could not load conversations.");
        return;
      }

      setPhoneNumArr([...data?.getPhonesInfoForText]);

      // If selected phn has new msgs, trigger requery event
      data?.getPhonesInfoForText?.forEach((obj) => {
        if (obj._id === selectedIdRef.current) {
          if (obj?.unreadMessages) {
            setQueryPhoneMsgTrigger((prev) => prev + 1);
          }
          return;
        }
      });
    } catch (err) {
      console.error("Messages inbox load failed:", err?.message || err);
      setLoadError(err?.message || "Could not load conversations.");
    } finally {
      setMsgLoading(false);
    }
  };

  /*
   * B3: the kebab menu could anchor to the WRONG ROW.
   *
   * This used to read `subMenuRefs.current[index]` — an array of DOM nodes
   * keyed by list POSITION, on a list that re-sorts by `recentTime` on every
   * SSE event. An inbound message while the menu was open re-sorted the list,
   * so the node `anchorEl` pointed at then belonged to a different thread.
   * (`menuPhn` is a snapshot, so it still ACTED on the right number — the
   * menu just lied about which row it was attached to.)
   *
   * The click already carries its own element, so the whole ref array is
   * gone: the anchor is `event.currentTarget` and the number is passed
   * directly. Nothing to keep in sync, nothing to go stale.
   */
  const handleAnchorElContactOptMenu = async (anchor, num) => {
    if (anchor === true) {
      setAnchorEl(null);
      setMenuPhn(null);
      return;
    }

    setAnchorEl(anchor);
    setMenuPhn(num || null);
  };

  const handlePhnUpdate = (newObj) => {
    setPhoneNumArr((prev) => {
      const update = prev?.map((prevObj) => {
        if (prevObj?._id === newObj._id) {
          return newObj;
        }
        return prevObj;
      });

      update?.sort((a, b) => {
        return parseInt(b?.recentTime) - parseInt(a?.recentTime);
      });

      return [...update];
    });
  };

  /* Owners, resolved once per row. Server-batched reverse-ref lookup
     (`owners` on each thread) — the fragile title-split is long gone. A shared
     number lists every owner; CLIENT owners can be navigated to, staff
     (`ownerType === "user"`) cannot, and carry a (Staff) tag so a named row
     with no button reads as explained rather than broken. */
  const ownerInfo = (num) => {
    const owners = num?.owners || [];
    const clientOwners = owners.filter((o) => o?.ownerType === "client");
    const primary = clientOwners[0] || owners[0] || null;
    const fullName = (o) =>
      `${o?.firstName || ""} ${o?.lastName || ""}`.trim();
    const primaryName = primary ? fullName(primary) : "";
    return {
      owners,
      clientOwners,
      fullName,
      name: primaryName
        ? primary.ownerType === "user"
          ? `${primaryName} (Staff)`
          : primaryName
        : null,
    };
  };

  return (
    <div className="text-msgs-page">
      {msgLoading && <AdminBDRSpinner message={"Loading..."} />}
      <MsgPageContactOptions
        handleClose={handleAnchorElContactOptMenu}
        anchorEl={anchorEl}
        phnId={menuPhn?._id || ""}
        handleUpdate={handlePhnUpdate}
        handleNewClientDisplay={handleNewClientDisplay}
      />
      {/* The client form is a MODAL now, not an inline panel that replaced
          the thread list. It is rendered unconditionally and gates on `open`
          so BdrModal owns its own mount/unmount. */}
      <ClientFormModal
        open={createNewClientDisplay}
        onClose={() => handleNewClientDisplay(false)}
        passedPhnObj={createClientPhnObj}
        onCreated={() => {
          /* Refresh the thread list so the newly-linked number shows its
             client name, then close. The old path snacked here through an
             optional prop that MessagesPage never passed, so it threw after
             a successful write and left the form stuck open. */
          queryStrippedPhoneInfo(false);
          handleNewClientDisplay(false);
        }}
      />
      {/*
        P4 — WHY A MODAL AND NOT MORE BUTTONS.

        A shared number rendered ONE button per client owner, each labelled
        `View {firstName}`. Three owners gave three buttons, and two owners
        who share a first name gave two IDENTICAL ones ("View Taylor",
        "View Taylor") — correct behaviour that read as a React key bug. The
        row is also unbounded: three today, N tomorrow, inside a 35% rail that
        WEB_STYLE_GUIDE.md:91 says will overflow a phone.

        So: one owner gets a button with that person's FULL name (the
        disambiguating data was already fetched and thrown away), and several
        get one "Assigned Contacts" button that opens this list.
      */}
      <BdrModal
        open={!!contactsFor}
        onClose={() => setContactsFor(null)}
        title="Assigned Contacts"
        variant="panel"
        size="sm"
      >
        <p className="text-msgs-contacts-num">{contactsFor?.phoneNumber}</p>
        <ul className="text-msgs-contacts-list">
          {(contactsFor?.owners || []).map((owner) => {
            const isClient = owner?.ownerType === "client";
            const label =
              `${owner?.firstName || ""} ${owner?.lastName || ""}`.trim() ||
              "Unnamed contact";
            return (
              <li
                className="text-msgs-contacts-row"
                key={`contact-${contactsFor?._id}-${owner?.ownerId}`}
              >
                <span className="text-msgs-contacts-name">
                  {label}
                  {!isClient && (
                    <span className="text-msgs-contacts-tag"> (Staff)</span>
                  )}
                </span>
                {/* Staff have no client page to open — the tag says why. */}
                {isClient && (
                  <BdrButton
                    size="sm"
                    variant="action"
                    onClick={() => {
                      setContactsFor(null);
                      navClient(owner?.ownerId);
                    }}
                  >
                    View Client
                  </BdrButton>
                )}
              </li>
            );
          })}
        </ul>
      </BdrModal>
      <div className="text-msgs-container bdr-section-panel">
        <div className="bdr-section-panel-hdr">
          <h2 className="bdr-section-panel-title">Messages</h2>
          <div className="bdr-section-panel-actions">
            <span className="text-msgs-count">
              {phoneNumArr?.length > 0
                ? `${phoneNumArr.length} conversation${
                    phoneNumArr.length === 1 ? "" : "s"
                  }`
                : ""}
            </span>
          </div>
        </div>
        {/* The two-pane row. Its own level, so the panel above can be the
            column that holds the header — and like every level in this page
            it is `flex: 1 1 auto` + an EXPLICIT `min-height: 0`. */}
        <div className="text-msgs-body">
          <div className="text-msgs-numbers-box bdr-grad-container-heavy bdr-border-thin bdr-shadow-subtle">
            {/*
              The error sits ABOVE the list, not inside its empty branch.
              Five of the six callers of `queryStrippedPhoneInfo` run while the
              list is ALREADY populated (a new conversation, an SSE reconnect,
              an sms_sent for an unknown thread, this retry, a client created)
              — put the alert in the empty branch and every one of those fails
              silently behind a stale list.

              Its own element, NOT `.bdr-empty`: that class is a row flex at
              0.45 opacity, italic, for "a state, not a record". An actionable
              error is a record, and a full-width alert plus a button inside a
              35%-wide row-flex would sit side by side and half-faded.
            */}
            {loadError && (
              <div className="text-msgs-load-error">
                <p
                  className="bdr-alert bdr-alert--danger bdr-alert--start"
                  role="alert"
                >
                  {loadError}
                </p>
                <BdrButton
                  size="sm"
                  variant="action"
                  onClick={() => {
                    setMsgLoading(true);
                    queryStrippedPhoneInfo(false);
                  }}
                >
                  Try again
                </BdrButton>
              </div>
            )}

            {phoneNumArr?.length > 0 ? (
              phoneNumArr.map((num, index) => {
                const { owners, clientOwners, fullName, name } =
                  ownerInfo(num);
                const unread = num?.unreadMessages === true;
                return (
                  <div
                    key={num?._id || `text-msgs-number-item-${index}`}
                    className={
                      selectedId === num?._id
                        ? "text-msgs-numbers-item bdr-hover-frost text-item-selected"
                        : "text-msgs-numbers-item bdr-hover-frost"
                    }
                    onClick={() => {
                      setSelectedId(num?._id);
                      clearUnreadLocal(num?._id);
                    }}
                  >
                    <div className="text-msgs-item-name-opt-container">
                      {/* ONE row shape for both cases. The named and unnamed
                          branches used to be separate blocks that each carried
                          their own copy of the kebab — two places to keep in
                          step, and they had already drifted apart in markup. */}
                      <div className="text-msgs-item-has-name-box">
                        <div
                          className={
                            unread
                              ? "text-msgs-item-title text-title-strong"
                              : "text-msgs-item-title"
                          }
                        >
                          {name || num?.phoneNumber}
                        </div>
                        {name && (
                          <div className="text-msgs-item-num">
                            {num?.phoneNumber}
                          </div>
                        )}
                      </div>
                      <div className="text-msgs-item-actions">
                        {owners.length > 1 ? (
                          <BdrButton
                            size="sm"
                            variant="action"
                            className="text-view-client-btn"
                            onClick={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              setContactsFor(num);
                            }}
                          >
                            Assigned Contacts
                          </BdrButton>
                        ) : (
                          clientOwners.length === 1 && (
                            <BdrButton
                              size="sm"
                              variant="action"
                              className="text-view-client-btn"
                              onClick={(event) => {
                                event.stopPropagation();
                                event.preventDefault();
                                navClient(clientOwners[0]?.ownerId);
                              }}
                            >
                              {fullName(clientOwners[0]) || "View Client"}
                            </BdrButton>
                          )
                        )}
                        <BdrButton
                          size="sm"
                          variant="plain"
                          iconOnly
                          icon={<MoreVertIcon />}
                          ariaLabel="Thread options"
                          className="text-view-client-btn"
                          /* The row behind this opens the thread. Without
                             stopping here the kebab opens the menu AND loads
                             the thread under it. */
                          onClick={(event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            handleAnchorElContactOptMenu(
                              event.currentTarget,
                              num
                            );
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              /* `.bdr-empty` IS right here, unlike for the error above: this
                 is a state, not a record the user has to act on. */
              <p className="bdr-empty">No Messages To Display</p>
            )}
          </div>
          <div className="text-msgs-selected-box">
            <MsgPageTextDisplay
              queryPhoneMsgTrigger={queryPhoneMsgTrigger}
              phoneId={selectedId}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
